// ImageLightboxModal.js
import React, { useCallback, useEffect } from 'react';
import Modal from 'react-modal';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import utils from '../../services/utilities.js';

Modal.setAppElement('#root');

// shots: the list of sibling shots (within the same scene) that have an image,
// used for left/right navigation. currentIndex is this shot's position in that
// list, or null when the lightbox is closed.
function ImageLightboxModal({ shots, sceneNumber, currentIndex, onNavigate, onClose }) {
  const isOpen = currentIndex !== null && currentIndex >= 0 && !!shots?.length;
  const shot = isOpen ? shots[currentIndex] : null;

  const goTo = useCallback(
    (delta) => {
      if (!shots?.length || currentIndex === null) return;
      const next = currentIndex + delta;
      if (next < 0 || next >= shots.length) return;
      onNavigate(next);
    },
    [shots, currentIndex, onNavigate],
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
      else if (event.key === 'ArrowLeft') goTo(-1);
      else if (event.key === 'ArrowRight') goTo(1);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, goTo]);

  if (!shot) return null;

  const filename = utils.buildShotImageFilename(sceneNumber, shot.shot_number);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < shots.length - 1;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="imageLightboxModal"
      overlayClassName="imageLightboxOverlay"
      contentLabel="Storyboard image viewer"
    >
      <div className="imageLightboxHeader">
        <span className="imageLightboxLabel">
          Scene {sceneNumber ?? '—'}, Shot {shot.shot_number ?? '—'}
        </span>
        <div className="imageLightboxActions">
          <IconButton
            aria-label="Download image"
            onClick={() => utils.downloadFile(shot.image, filename)}
            sx={{ color: '#fff' }}
          >
            <FileDownloadIcon />
          </IconButton>
          <IconButton aria-label="Close" onClick={onClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </div>
      </div>

      <div className="imageLightboxBody">
        <IconButton
          aria-label="Previous frame"
          className="imageLightboxNav imageLightboxNavLeft"
          onClick={() => goTo(-1)}
          disabled={!hasPrev}
          sx={{ color: '#fff', visibility: hasPrev ? 'visible' : 'hidden' }}
        >
          <ChevronLeftIcon fontSize="large" />
        </IconButton>

        <img src={shot.image} alt={filename} className="imageLightboxImage" />

        <IconButton
          aria-label="Next frame"
          className="imageLightboxNav imageLightboxNavRight"
          onClick={() => goTo(1)}
          disabled={!hasNext}
          sx={{ color: '#fff', visibility: hasNext ? 'visible' : 'hidden' }}
        >
          <ChevronRightIcon fontSize="large" />
        </IconButton>
      </div>
    </Modal>
  );
}

export default ImageLightboxModal;
